<script setup lang="ts">
import { onErrorCaptured, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { isAppError } from '@shared/api/errors'
import { captureError } from '@shared/observability'
import BaseButton from '@shared/components/base/BaseButton.vue'

/** Catches render/setup errors below it so a broken screen never blanks the whole app. */
const failed = ref(false)
const correlationId = ref<string>()
const route = useRoute()
const { t } = useI18n()

onErrorCaptured((err) => {
  failed.value = true
  correlationId.value = isAppError(err) ? err.correlationId : undefined
  captureError(err, { correlationId: correlationId.value, route: String(route.name ?? '') })
  return false
})

watch(
  () => route.fullPath,
  () => (failed.value = false),
)
</script>

<template>
  <div
    v-if="failed"
    class="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center"
    role="alert"
  >
    <h1 class="font-display text-section font-semibold text-ink">
      {{ t('errors.unexpected.title') }}
    </h1>
    <p class="max-w-md text-sm text-ink-secondary">{{ t('errors.unexpected.message') }}</p>
    <p v-if="correlationId" class="text-xs text-ink-secondary">
      {{ t('errors.correlationId', { id: correlationId }) }}
    </p>
    <BaseButton @click="failed = false">{{ t('common.retry') }}</BaseButton>
  </div>
  <slot v-else />
</template>
